import { useState, useEffect, useRef } from 'react'
import { IoDocument } from "react-icons/io5";
import { FaXmark, FaFilePdf, FaFileExcel } from "react-icons/fa6";
import { HiArrowDownTray } from "react-icons/hi2";
import { toast } from 'react-toastify';
import HTSCard from '../comps/HTSCard'
import PartInfoCard from './PartInfoCard'
import IndividualMaterialSummary from './IndividualMaterialSummary'
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import TariffCalculator from './TariffCalculator'
import ThumbnailImage from './ThumbnailImage';
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import WeightResult from './WeightResult'

// ---------------------------------------------------------------------------
// Drill-Down helpers
// ---------------------------------------------------------------------------

const DD_LOG_COLORS = {
  phase:   'text-[#f26522]',
  success: 'text-[#94c43d]',
  info:    'text-gray-400',
  warning: 'text-[#f26522]',
  error:   'text-red-400',
}

/** Markdown renderer with consistent inline styles for the drill-down panel */
function Md({ children }) {
  if (!children) return null
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p:          ({ node, ...p }) => <p          className="text-gray-700 mb-2 leading-relaxed text-sm last:mb-0" {...p} />,
        strong:     ({ node, ...p }) => <strong     className="font-semibold text-gray-900" {...p} />,
        em:         ({ node, ...p }) => <em         className="italic text-gray-700" {...p} />,
        code:       ({ node, className, children, ...p }) => {
          const isInline = !className && !String(children ?? '').includes('\n')
          return isInline
            ? <code className="bg-gray-100 text-indigo-700 px-1 py-0.5 rounded text-xs font-mono" {...p}>{children}</code>
            : <pre className="bg-gray-100 rounded p-2 text-xs font-mono overflow-x-auto my-2 whitespace-pre-wrap"><code className={className} {...p}>{children}</code></pre>
        },
        ul:         ({ node, ...p }) => <ul         className="list-disc list-inside space-y-0.5 mb-2 text-sm" {...p} />,
        ol:         ({ node, ...p }) => <ol         className="list-decimal list-inside space-y-0.5 mb-2 text-sm" {...p} />,
        li:         ({ node, ...p }) => <li         className="text-gray-700" {...p} />,
        blockquote: ({ node, ...p }) => <blockquote className="border-l-2 border-indigo-400 pl-3 italic text-gray-500 my-2 text-sm" {...p} />,
        table:      ({ node, ...p }) => <div className="overflow-x-auto my-2"><table className="text-xs border-collapse w-full" {...p} /></div>,
        th:         ({ node, ...p }) => <th         className="border border-gray-300 bg-gray-50 px-2 py-1 font-semibold text-left text-xs" {...p} />,
        td:         ({ node, ...p }) => <td         className="border border-gray-300 px-2 py-1 text-gray-700 text-xs" {...p} />,
      }}
    >
      {children}
    </ReactMarkdown>
  )
}

const DIFF_BADGE = {
  Easy:   'bg-green-100 text-green-700',
  Medium: 'bg-amber-100 text-amber-700',
  Hard:   'bg-red-100 text-red-700',
}

/** Fully formatted drill-down result panel */
function DrillDownPanel({ data }) {
  const [open, setOpen] = useState({
    analysis:  true,
    impact:    true,
    solutions: true,
    concerns:  false,
    rationale: false,
  })
  const toggle = key => setOpen(s => ({ ...s, [key]: !s[key] }))

  const Section = ({ id, label, borderColor, headerBg, headerText, children }) => (
    <div className={`border rounded-lg overflow-hidden mb-2 ${borderColor}`}>
      <button
        onClick={() => toggle(id)}
        className={`w-full flex items-center justify-between px-3 py-2 text-left ${headerBg}`}
      >
        <span className={`font-semibold text-xs uppercase tracking-wide ${headerText}`}>{label}</span>
        <span className={`text-xs ${headerText}`}>{open[id] ? '▲' : '▼'}</span>
      </button>
      {open[id] && <div className="px-3 py-3">{children}</div>}
    </div>
  )

  return (
    <div className="mt-3 text-sm">

      {/* Summary headline */}
      {data.summary_headline && (
        <div className="mb-3 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium leading-snug">
          🔍 {data.summary_headline}
        </div>
      )}

      {/* Deep Analysis */}
      <Section
        id="analysis" label="Deep Analysis"
        borderColor="border-indigo-200" headerBg="bg-indigo-50" headerText="text-indigo-700"
      >
        <Md>{data.deep_analysis}</Md>
      </Section>

      {/* Impact Assessment */}
      {data.impact_assessment && (
        <Section
          id="impact" label="Impact Assessment"
          borderColor="border-blue-200" headerBg="bg-blue-50" headerText="text-blue-700"
        >
          <div className="grid grid-cols-2 gap-2">
            {[
              { key: 'manufacturing', label: 'Manufacturing', icon: '⚙️' },
              { key: 'assembly',      label: 'Assembly',      icon: '🔩' },
              { key: 'function',      label: 'Function',      icon: '📐' },
              { key: 'reliability',   label: 'Reliability',   icon: '🔬' },
            ].map(({ key, label, icon }) => (
              <div key={key} className="bg-gray-50 rounded p-2 border border-gray-200">
                <div className="text-xs font-semibold text-gray-500 mb-1">{icon} {label}</div>
                <Md>{data.impact_assessment[key]}</Md>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Solution Options */}
      {data.solution_options?.length > 0 && (
        <Section
          id="solutions" label={`Solution Options (${data.solution_options.length})`}
          borderColor="border-green-200" headerBg="bg-green-50" headerText="text-green-700"
        >
          <div className="space-y-3">
            {data.solution_options.map((sol, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">{sol.id}: {sol.title}</span>
                    {sol.am_relevant && (
                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded font-medium">AM</span>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${DIFF_BADGE[sol.difficulty] || 'bg-gray-100 text-gray-600'}`}>
                    {sol.difficulty}
                  </span>
                </div>
                <Md>{sol.description}</Md>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <div className="text-xs font-semibold text-green-700 mb-1">Pros</div>
                    <ul className="space-y-0.5">
                      {(sol.pros || []).map((p, pi) => (
                        <li key={pi} className="text-xs text-gray-700 flex gap-1">
                          <span className="text-green-500 shrink-0">✓</span>
                          <Md>{p}</Md>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-red-600 mb-1">Cons</div>
                    <ul className="space-y-0.5">
                      {(sol.cons || []).map((c, ci) => (
                        <li key={ci} className="text-xs text-gray-700 flex gap-1">
                          <span className="text-red-400 shrink-0">✗</span>
                          <Md>{c}</Md>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                {sol.am_relevant && sol.am_notes && (
                  <div className="mt-2 px-2 py-1.5 bg-purple-50 border border-purple-200 rounded text-xs">
                    <span className="font-semibold text-purple-700">AM Notes: </span>
                    <Md>{sol.am_notes}</Md>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Related Concerns */}
      {data.related_concerns?.length > 0 && (
        <Section
          id="concerns" label={`Related Concerns (${data.related_concerns.length})`}
          borderColor="border-amber-200" headerBg="bg-amber-50" headerText="text-amber-700"
        >
          <div className="space-y-2">
            {data.related_concerns.map((c, i) => (
              <div key={i} className="flex gap-3 p-2 bg-amber-50 rounded border border-amber-100">
                <div className="shrink-0 text-amber-500 mt-0.5">⚠</div>
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 text-xs mb-0.5">{c.title}</div>
                  <Md>{c.description}</Md>
                  {c.check_location && (
                    <div className="text-xs text-gray-500 mt-1">
                      📍 Check: <span className="font-mono text-gray-700">{c.check_location}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Engineering Rationale + Recommended Action */}
      <Section
        id="rationale" label="Engineering Rationale"
        borderColor="border-gray-200" headerBg="bg-gray-50" headerText="text-gray-600"
      >
        <Md>{data.engineering_rationale}</Md>
        {data.recommended_action && (
          <div className="mt-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
            <div className="text-xs font-semibold text-green-700 mb-1">Recommended Action</div>
            <Md>{data.recommended_action}</Md>
          </div>
        )}
      </Section>

    </div>
  )
}

export default function FileDetailPanel({
  file,
  fileId,
  filename,
  onClose,
  defaultTab,
  isLoading,
  error,
}) {
  const location = useLocation()
  const { folder_name, space_path } = location.state?.state || {};
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [activeTab, setActiveTab] = useState(defaultTab || 'tariff')
  const [hasAutoSelected, setHasAutoSelected] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  // V2 Drawing Checker state
  const [feedbackState, setFeedbackState] = useState({}) // { [issueId]: 'interesting'|'not_interesting'|'neutral' }
  const [drillDownState, setDrillDownState] = useState({}) // { [issueId]: { loading, logs, data, error, collapsed } }
  const drillDownTimersRef = useRef({}) // { [issueId]: [timerId, ...] }
  const [annotationOverride, setAnnotationOverride] = useState(null) // { annotation_status, annotated_image_urls, annotated_thumbnail_base64 }

  const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;
  const GETANALYSED_API = `${BASE_URL}/ocr/getanalysedfiles/`;
  const token = sessionStorage.getItem("userToken");

  // Debug logging
  console.log('=== FileDetail Component Loaded ===');
  console.log('process.env.REACT_APP_BE_MAIN_URL:', process.env.REACT_APP_BE_MAIN_URL);
  console.log('BASE_URL:', BASE_URL);
  console.log('GETANALYSED_API:', GETANALYSED_API);

  // Log for debugging
  console.log('FileDetail BASE_URL:', BASE_URL);

  const selectedFile = files.find(f => f.id === fileId);
  const fetchAnalysisData = async () => {
    try {
      setLoading(true);
      // Always include filename as fallback, add doc_id for precise lookup
      let url = GETANALYSED_API + `?filename=${encodeURIComponent(file.filename)}`;
      if (file._id) {
        // Add doc_id for precise document lookup
        url += `&doc_id=${encodeURIComponent(file._id)}`;
      }
      if (folder_name) {
        url += `&folder_name=${encodeURIComponent(folder_name)}`;
      }
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allFiles = res.data.files || [];
      setFiles(allFiles);

      // If we queried by doc_id and got a result, use it directly
      if (file._id && allFiles.length === 1) {
        setData(allFiles[0]);
        return;
      }

      // Otherwise find the best matching document
      let matchedFile = allFiles[0]; // Default to first
      if (allFiles.length > 1) {
        // First try to match by folder_name
        if (folder_name) {
          const folderMatches = allFiles.filter(f => f.folder_name === folder_name);
          if (folderMatches.length === 1) {
            matchedFile = folderMatches[0];
          } else if (folderMatches.length > 1) {
            // Multiple matches in same folder - prefer one with more analysis types
            matchedFile = folderMatches.reduce((best, current) => {
              const bestCount = Object.keys(best.analyses || {}).length;
              const currentCount = Object.keys(current.analyses || {}).length;
              return currentCount > bestCount ? current : best;
            });
          }
        }
        // If no folder match, prefer document with most analysis types
        if (!matchedFile || matchedFile === allFiles[0]) {
          matchedFile = allFiles.reduce((best, current) => {
            const bestCount = Object.keys(best.analyses || {}).length;
            const currentCount = Object.keys(current.analyses || {}).length;
            return currentCount > bestCount ? current : best;
          });
        }
      }
      setData(matchedFile);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Excel export for individual file
  const handleExportExcel = async () => {
    if (!data?._id) {
      toast.error('No file data available for export');
      return;
    }

    setIsExporting(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/ocr/export/excel/individual/${data._id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let downloadFilename = `${file.filename.replace(/\.[^/.]+$/, '')}_Tariff_Analysis.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) downloadFilename = match[1];
      }

      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Excel exported successfully');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export Excel file');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle Excel export for Design Review
  const handleExportDesignReview = async () => {
    if (!data?._id) {
      toast.error('No file data available for export');
      return;
    }

    setIsExporting(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/ocr/export/excel/design-review/${data._id}/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let downloadFilename = `${file.filename.replace(/\.[^/.]+$/, '')}_Design_Review.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) downloadFilename = match[1];
      }

      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Design Review exported successfully');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export Design Review Excel file');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle Excel export for Drawing Checker
  const handleExportDrawingCheckerExcel = async (playType = 'veeco_drawing_checker_v1') => {
    if (!data?._id) {
      toast.error('No file data available for export');
      return;
    }

    if (!data?.analyses?.[playType]) {
      toast.error(`No ${playType} analysis found for this file.`);
      return;
    }

    setIsExporting(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/ocr/export/excel/drawing-checker/${data._id}/?play_type=${playType}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let downloadFilename = `${file.filename.replace(/\.[^/.]+$/, '')}_Drawing_Checker.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) downloadFilename = match[1];
      }

      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Drawing Checker exported successfully');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Failed to export Drawing Checker Excel file');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle viewing Drawing Checker full report (opens in new tab)
  const handleViewDrawingCheckerReport = async (playType = 'veeco_drawing_checker_v1') => {
    if (!data?._id) {
      toast.error('No file data available');
      return;
    }

    // Log available analyses for debugging
    console.log('=== View Drawing Checker Report Debug ===');
    console.log('Available analyses:', Object.keys(data?.analyses || {}));
    console.log('Requested play type:', playType);
    console.log('Analysis exists?', !!data?.analyses?.[playType]);
    console.log('File ID:', data._id);

    // Check if analysis exists for this play type
    if (!data?.analyses?.[playType]) {
      toast.error(`No ${playType} analysis found for this file. Please run the analysis first.`);
      return;
    }

    const reportUrl = `${BASE_URL}/ocr/export/drawing-checker/${data._id}/?play_type=${playType}`;
    console.log('View Report - Full URL:', reportUrl);
    console.log('View Report - BASE_URL:', BASE_URL);
    console.log('View Report - Play Type:', playType);
    
    const newWindow = window.open('about:blank', '_blank');
    if (!newWindow) {
      toast.error('Please allow popups for this site to view the report');
      return;
    }

    // Show loading message
    newWindow.document.write('<html><body><h2>Loading report...</h2></body></html>');

    try {
      // Fetch HTML report with authentication
      const response = await axios.get(reportUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'text'
      });

      console.log('View Report - Response received, length:', response.data.length);

      // Fix relative URLs in the HTML to point to backend
      let htmlContent = response.data;
      
      // Replace relative /ocr/ URLs with absolute backend URLs
      htmlContent = htmlContent.replace(
        /href=["']\/ocr\//g, 
        `href="${BASE_URL}/ocr/`
      );
      htmlContent = htmlContent.replace(
        /src=["']\/ocr\//g, 
        `src="${BASE_URL}/ocr/`
      );
      
      // Inject JavaScript to handle authenticated downloads
      const authScript = `
        <script>
          (function() {
            const token = '${token}';
            const baseUrl = '${BASE_URL}';
            const playType = '${playType}';

            // Expose token and baseUrl for feedback/drill-down buttons
            window.__authToken = token;
            window.__baseUrl = baseUrl;

            console.log('=== Injected Download Handler ===');
            console.log('Play Type:', playType);
            console.log('Base URL:', baseUrl);
            
            // Intercept clicks on download links
            document.addEventListener('click', function(e) {
              const link = e.target.closest('a[href*="/ocr/download/"]');
              if (link) {
                e.preventDefault();
                let url = link.href;
                
                // Add play_type parameter if not present
                if (!url.includes('play_type=')) {
                  const separator = url.includes('?') ? '&' : '?';
                  url = url + separator + 'play_type=' + encodeURIComponent(playType);
                }
                
                console.log('Download URL with play_type:', url);
                
                // Fetch with authentication
                fetch(url, {
                  headers: { 'Authorization': 'Bearer ' + token }
                })
                .then(response => {
                  console.log('Download response status:', response.status);
                  if (!response.ok) {
                    return response.text().then(text => {
                      console.error('Download failed - Response:', text);
                      try {
                        const errorData = JSON.parse(text);
                        throw new Error(errorData.error || errorData.detail || 'Download failed');
                      } catch (e) {
                        throw new Error(text || 'Download failed with status ' + response.status);
                      }
                    });
                  }
                  
                  // Get filename from Content-Disposition header
                  const disposition = response.headers.get('Content-Disposition');
                  let filename = 'Drawing_Checker_Report.pdf';
                  if (disposition && disposition.includes('filename=')) {
                    const matches = disposition.match(/filename="?(.+?)"?$/);
                    if (matches && matches[1]) {
                      filename = matches[1];
                    }
                  }
                  
                  return response.blob().then(blob => ({ blob, filename }));
                })
                .then(({ blob, filename }) => {
                  // Create download link
                  const downloadUrl = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = downloadUrl;
                  a.download = filename;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(downloadUrl);
                  console.log('Download successful:', filename);
                })
                .catch(err => {
                  console.error('Download error:', err);
                  alert('Failed to download report: ' + err.message + '\\n\\nPlay type: ' + playType + '\\n\\nThis may be a backend issue.');
                });
              }
            });
          })();
        </script>
      `;
      
      // Inject the script before closing </body> tag
      htmlContent = htmlContent.replace('</body>', authScript + '</body>');
      
      console.log('View Report - HTML URLs fixed and auth script injected');

      // Write the modified HTML content to the new window
      newWindow.document.open();
      newWindow.document.write(htmlContent);
      newWindow.document.close();
    } catch (err) {
      console.error('Failed to load report:', err);
      console.error('Error details:', err.response?.status, err.response?.data);
      console.error('Request URL:', reportUrl);
      console.error('Play type sent:', playType);
      
      // Show more specific error message
      let errorMessage = 'Failed to load Drawing Checker report';
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        }
      }
      
      // Add context to error message
      errorMessage = `Backend Error: ${errorMessage}\n\nRequested play type: ${playType}\nThis might be a backend issue with the play_type parameter.`;
      
      toast.error(errorMessage);
      newWindow.close();
    }
  };

  // Handle downloading Drawing Checker report
  const handleDownloadDrawingCheckerReport = async (playType = 'veeco_drawing_checker_v1') => {
    if (!data?._id) {
      toast.error('No file data available for export');
      return;
    }

    // Log available analyses for debugging
    console.log('=== Download Drawing Checker Report Debug ===');
    console.log('Available analyses:', Object.keys(data?.analyses || {}));
    console.log('Requested play type:', playType);
    console.log('Analysis exists?', !!data?.analyses?.[playType]);
    console.log('File ID:', data._id);

    // Check if analysis exists for this play type
    if (!data?.analyses?.[playType]) {
      toast.error(`No ${playType} analysis found for this file. Please run the analysis first.`);
      return;
    }

    setIsExporting(true);

    const downloadUrl = `${BASE_URL}/ocr/download/drawing-checker/${data._id}/?play_type=${playType}`;
    console.log('Download Report - Full URL:', downloadUrl);
    console.log('Download Report - BASE_URL:', BASE_URL);
    console.log('Download Report - Play Type:', playType);

    try {
      const response = await axios.get(downloadUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      console.log('Download Report - Response received');

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or generate one
      const contentDisposition = response.headers['content-disposition'];
      let downloadFilename = `${file.filename.replace(/\.[^/.]+$/, '')}_Drawing_Checker_Report.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) downloadFilename = match[1];
      }

      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Drawing Checker report downloaded successfully');
    } catch (err) {
      console.error('Download failed:', err);
      console.error('Error details:', err.response?.status, err.response?.data);
      console.error('Request URL:', downloadUrl);
      console.error('Play type sent:', playType);
      
      // Show more specific error message
      let errorMessage = 'Failed to download Drawing Checker report';
      if (err.response?.data) {
        // Handle blob error response
        if (err.response.data instanceof Blob) {
          // Convert blob to text to read error message
          err.response.data.text().then(text => {
            try {
              const errorData = JSON.parse(text);
              const detailedError = errorData.error || errorData.detail || text;
              toast.error(`Backend Error: ${detailedError}\n\nRequested: ${playType}\nThis appears to be a backend issue.`);
            } catch {
              toast.error(`Backend Error: ${text || errorMessage}\n\nRequested: ${playType}`);
            }
          });
          return;
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        }
      }
      
      errorMessage = `Backend Error: ${errorMessage}\n\nRequested play type: ${playType}\nThis might be a backend issue with the play_type parameter.`;
      toast.error(errorMessage);
    } finally {
      setIsExporting(false);
    }
  };

  const tariffData = data?.analyses?.tariff_v1
  const costData = data?.analyses?.cost_reduction_v1
  const designReviewData = data?.analyses?.veeco_design_review_v1
  const drawingCheckerData = data?.analyses?.veeco_drawing_checker_v1
  const drawingCheckerVeecoData = data?.analyses?.veeco_drawing_checker_veeco_v1
  const drawingCheckerGeneralData = data?.analyses?.veeco_drawing_checker_general_v1
  const drawingCheckerV2Data = data?.analyses?.veeco_drawing_checker_v2
  const weightData = data?.analyses?.weight_v1

  const tabs = [
    { id: 'tariff', label: 'Tariff Analysis', available: !!tariffData },
    { id: 'cost', label: 'Cost Reduction', available: !!costData },
    { id: 'design_review', label: 'Design Review', available: !!designReviewData },
    { id: 'drawing_checker_v2', label: 'Drawing Checker V2', available: !!drawingCheckerV2Data },
    { id: 'drawing_checker', label: 'Drawing Checker - Both', available: !!drawingCheckerData },
    { id: 'drawing_checker_veeco', label: 'Drawing Checker - Veeco', available: !!drawingCheckerVeecoData },
    { id: 'drawing_checker_general', label: 'Drawing Checker - General', available: !!drawingCheckerGeneralData },
    { id: 'weight', label: 'Weight Estimation', available: !!weightData },
    { id: 'calculator', label: 'Calculator', available: !!tariffData },
  ]

  useEffect(() => {
    if (!file) return
  }, [file])

  // Auto-select the first available tab when data loads
  useEffect(() => {
    if (!isLoading && !hasAutoSelected && data) {
      const currentTabAvailable = tabs.find(t => t.id === activeTab)?.available

      if (!currentTabAvailable) {
        const firstAvailable = tabs.find(t => t.available)
        if (firstAvailable) {
          setActiveTab(firstAvailable.id)
        }
      }
      setHasAutoSelected(true)
    }
  }, [isLoading, data, hasAutoSelected, activeTab, tabs])

  useEffect(() => {
    fetchAnalysisData();
  }, [filename]);

  // Poll for background annotation completion
  useEffect(() => {
    const annotationStatus = drawingCheckerV2Data?.data?.annotation_beta?.annotation_status
    if (annotationStatus !== 'pending') return

    const docId = file?._id
    if (!docId) return

    let cancelled = false
    const startTime = Date.now()
    const MAX_POLL_MS = 10 * 60 * 1000 // 10 minutes

    const poll = setInterval(async () => {
      if (cancelled) return
      if (Date.now() - startTime > MAX_POLL_MS) {
        clearInterval(poll)
        setAnnotationOverride({ annotation_status: 'failed', annotated_image_urls: [], annotated_thumbnail_base64: '' })
        return
      }
      try {
        const res = await axios.get(
          `${BASE_URL}/ocr/annotation-status/${docId}/?play_type=veeco_drawing_checker_v2`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        if (res.data.annotation_status === 'complete') {
          setAnnotationOverride(res.data)
          clearInterval(poll)
        } else if (res.data.annotation_status === 'failed') {
          setAnnotationOverride(res.data)
          clearInterval(poll)
        }
      } catch (err) {
        console.warn('Annotation status poll failed:', err)
      }
    }, 5000)

    return () => { cancelled = true; clearInterval(poll) }
  }, [drawingCheckerV2Data?.data?.annotation_beta?.annotation_status, file?._id])

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-end z-50">
      <div className="h-full w-full max-w-3xl bg-white shadow-xl overflow-hidden flex flex-col">
        <div className="flex items-center gap-4 px-6 py-4 border-b bg-green-800 text-white">
          {file?.base_metadata?.s3_url && (
            <div className="w-20 h-20 border-2 rounded-md overflow-hidden">
              <ThumbnailImage
                fileId={file._id}
                filename={filename}
                s3Url={file.base_metadata.s3_url}
              />
            </div>
          )}

          <div className="flex-1 min-w-0 ">
            <h2 className="text-lg text-white font-semibold truncate">{file.filename}</h2>
            <p className="text-lg text-white font-semibold">Analysis completed
              {/* {data?.status === 'analyzed' ? 'Analysis Complete' : 'Pending Analysis'} */}
            </p>
          </div>

          {/* Export button - show for tariff or design review tabs */}
          {((activeTab === 'tariff' && tariffData) ||
            (activeTab === 'design_review' && designReviewData)) && (
            <button
              onClick={() => {
                if (activeTab === 'design_review') {
                  handleExportDesignReview();
                } else {
                  handleExportExcel();
                }
              }}
              disabled={isExporting}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
              title={
                activeTab === 'design_review'
                  ? "Export Design Review to Excel"
                  : "Export to Excel"
              }
            >
              {isExporting ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <HiArrowDownTray className="w-6 h-6" />
              )}
            </button>
          )}

          {/* Drawing Checker export buttons - PDF and Excel */}
          {((activeTab === 'drawing_checker' && drawingCheckerData) ||
            (activeTab === 'drawing_checker_veeco' && drawingCheckerVeecoData) ||
            (activeTab === 'drawing_checker_general' && drawingCheckerGeneralData) ||
            (activeTab === 'drawing_checker_v2' && drawingCheckerV2Data)) && (
            <>
              <button
                onClick={() => {
                  const playType = activeTab === 'drawing_checker_veeco'
                    ? 'veeco_drawing_checker_veeco_v1'
                    : activeTab === 'drawing_checker_general'
                      ? 'veeco_drawing_checker_general_v1'
                      : activeTab === 'drawing_checker_v2'
                        ? 'veeco_drawing_checker_v2'
                        : 'veeco_drawing_checker_v1';
                  handleDownloadDrawingCheckerReport(playType);
                }}
                disabled={isExporting}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                title="Download Drawing Checker PDF Report"
              >
                {isExporting ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FaFilePdf className="w-6 h-6" />
                )}
              </button>
              <button
                onClick={() => {
                  const playType = activeTab === 'drawing_checker_veeco'
                    ? 'veeco_drawing_checker_veeco_v1'
                    : activeTab === 'drawing_checker_general'
                      ? 'veeco_drawing_checker_general_v1'
                      : activeTab === 'drawing_checker_v2'
                        ? 'veeco_drawing_checker_v2'
                        : 'veeco_drawing_checker_v1';
                  handleExportDrawingCheckerExcel(playType);
                }}
                disabled={isExporting}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                title="Export Drawing Checker to Excel"
              >
                {isExporting ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FaFileExcel className="w-6 h-6" />
                )}
              </button>
            </>
          )}

          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <FaXmark className="w-6 h-6" />
          </button>
        </div>
        <div className="flex flex-wrap border-b">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => tab.available && setActiveTab(tab.id)}
              disabled={!tab.available}
              className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${activeTab === tab.id
                ? 'text-black border-b-2 border-green-800'
                : tab.available
                  ? 'text-black hover:text-gray-700'
                  : 'text-gray-300 cursor-not-allowed'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-gray-500">Loading analysis...</div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              Failed to load analysis data
            </div>
          ) : (
            <>
              {/* Tariff Tab */}
              {activeTab === 'tariff' && tariffData?.data && (
                <div className="space-y-6">

                  {tariffData.data.tradeCompliance?.primaryHts && (
                    <HTSCard
                      primaryHts={tariffData.data.tradeCompliance.primaryHts}
                      alternativeHts={tariffData.data.tradeCompliance.alternativeHts}
                    />
                  )}

                  {/* Material Summary with Sec 232/301 indicators */}
                  {tariffData.data.materialSpecifications && (
                    <IndividualMaterialSummary
                      material={tariffData.data.materialSpecifications.material}
                      materialParent={tariffData.data.materialSpecifications.materialParent}
                      weightKg={tariffData.data.partInformation?.weightKg}
                      weightStatus={tariffData.data.partInformation?.weightStatus}
                      sec232={tariffData.data.tradeCompliance?.primaryHts?.sec232}
                      sec301={tariffData.data.tradeCompliance?.primaryHts?.sec301China}
                    />
                  )}

                  <PartInfoCard
                    partInfo={tariffData.data.partInformation || {}}
                    materialSpecs={tariffData.data.materialSpecifications}
                  />
                </div>
              )}

              {/* Cost Reduction Tab */}
              {activeTab === 'cost' && costData?.data && (
                <div className="space-y-6">
                  {costData.data && (
                    <div className="rounded-xl shadow-sm border border-gray-100
           overflow-hidden p-6 bg-amber-50 ">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Executive Summary
                      </h3>
                      <p className="text-gray-600">
                        {costData.data.executiveSummary || 'No summary available'}
                      </p>
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-green-800">
                            {costData.data.estimatedSavingsRange || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">Total Savings</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">
                            {costData.data.engineeringCount || 0}
                          </div>
                          <div className="text-xs text-gray-500">Engineering</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-purple-600">
                            {costData.data.supplyChainCount || 0}
                          </div>
                          <div className="text-xs text-gray-500">Supply Chain</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}


                  {costData.data.recommendations && costData.data.recommendations.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Recommendations
                      </h3>
                      <div className="space-y-4">
                        {costData.data.recommendations.map((rec, idx) => (
                          <div key={idx} className="rounded-xl shadow-sm border border-gray-100
           overflow-hidden p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`badge ${rec.ownership === 'Engineering'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-purple-100 text-purple-800'
                                    }`}
                                >
                                  {rec.ownership}
                                </span>
                                <span className="badge bg-gray-100 text-gray-800">
                                  {rec.category}
                                </span>
                                <span
                                  className={`badge ${rec.priority === 'High'
                                    ? 'badge-error'
                                    : rec.priority === 'Medium'
                                      ? 'badge-warning'
                                      : 'badge-info'
                                    }`}
                                >
                                  {rec.priority}
                                </span>
                              </div>
                              <span className="text-green-600 font-semibold">
                                {rec.estimatedImpact}
                              </span>
                            </div>
                            <h4 className="font-medium text-gray-900 mt-2">
                              {rec.title}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">
                              {rec.proposedChange}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Design Review Tab */}
              {activeTab === 'design_review' && designReviewData?.data && (
                <div className="space-y-6">
                  {/* Summary Stats */}
                  {designReviewData.data.summary && (
                    <div className="rounded-xl shadow-sm border border-gray-100 overflow-hidden p-6 bg-amber-50">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Review Summary
                      </h3>
                      <p className="text-gray-600 text-sm mb-4">
                        {designReviewData.data.drawing_description || 'Engineering design review'}
                      </p>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-gray-700">
                            {designReviewData.data.summary.total_checks || 0}
                          </div>
                          <div className="text-xs text-gray-500">Total Checks</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-green-800">
                            {designReviewData.data.summary.passed || 0}
                          </div>
                          <div className="text-xs text-gray-500">Passed</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-red-600">
                            {designReviewData.data.summary.failed || 0}
                          </div>
                          <div className="text-xs text-gray-500">Failed</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-purple-600">
                            {designReviewData.data.summary.pass_rate || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">Pass Rate</div>
                        </div>
                      </div>
                      {designReviewData.data.manufacturing_process && (
                        <div className="mt-4 text-sm text-gray-600">
                          <span className="font-medium">Manufacturing Process:</span> {designReviewData.data.manufacturing_process}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Review Results */}
                  {designReviewData.data.results && designReviewData.data.results.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Review Results
                      </h3>
                      <div className="space-y-4">
                        {designReviewData.data.results.map((result, idx) => (
                          <div
                            key={idx}
                            className={`rounded-xl shadow-sm border overflow-hidden p-4 ${
                              result.status === 'PASS'
                                ? 'border-green-200 bg-green-50/50'
                                : 'border-red-200 bg-red-50/50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`px-2 py-1 rounded-md text-xs font-medium ${
                                    result.type === 'General'
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-purple-100 text-purple-800'
                                  }`}
                                >
                                  {result.type}
                                </span>
                                <span
                                  className={`px-2 py-1 rounded-md text-xs font-bold ${
                                    result.status === 'PASS'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {result.status}
                                </span>
                              </div>
                            </div>
                            <h4 className="font-medium text-gray-900 mt-2">
                              {result.check}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">
                              {result.observation}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Drawing Checker Tab */}
              {activeTab === 'drawing_checker' && drawingCheckerData?.data && (
                <div className="space-y-6">
                  {/* Quality Score Summary */}
                  {drawingCheckerData.data.summary && (
                    <div className="rounded-xl shadow-sm border border-gray-100 overflow-hidden p-6 bg-amber-50">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Quality Summary
                        </h3>
                        <button
                          onClick={() => handleViewDrawingCheckerReport('veeco_drawing_checker_v1')}
                          className="px-4 py-2 bg-green-800 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                        >
                          View Full Report
                        </button>
                      </div>
                      <p className="text-gray-600 text-sm mb-4">
                        {drawingCheckerData.data.drawing_info?.description || 'Engineering drawing validation'}
                      </p>
                      <div className="grid grid-cols-5 gap-4">
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className={`text-3xl font-bold ${
                            (drawingCheckerData.data.summary.quality_score || 0) >= 7
                              ? 'text-green-600'
                              : (drawingCheckerData.data.summary.quality_score || 0) >= 5
                                ? 'text-yellow-600'
                                : 'text-red-600'
                          }`}>
                            {drawingCheckerData.data.summary.quality_score || 0}
                            <span className="text-sm text-gray-400">/{drawingCheckerData.data.summary.quality_max || 10}</span>
                          </div>
                          <div className="text-xs text-gray-500">Quality Score</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-red-600">
                            {drawingCheckerData.data.summary.critical || 0}
                          </div>
                          <div className="text-xs text-gray-500">Critical</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-orange-600">
                            {drawingCheckerData.data.summary.major || 0}
                          </div>
                          <div className="text-xs text-gray-500">Major</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-green-600">
                            {drawingCheckerData.data.summary.minor || 0}
                          </div>
                          <div className="text-xs text-gray-500">Minor</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-gray-600">
                            {drawingCheckerData.data.summary.total_issues || 0}
                          </div>
                          <div className="text-xs text-gray-500">Total Issues</div>
                        </div>
                      </div>
                      {drawingCheckerData.data.drawing_info && (
                        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                          {drawingCheckerData.data.drawing_info.drawing_number && (
                            <div>
                              <span className="font-medium text-gray-700">Drawing #:</span>{' '}
                              <span className="text-gray-600">{drawingCheckerData.data.drawing_info.drawing_number}</span>
                            </div>
                          )}
                          {drawingCheckerData.data.drawing_info.revision && (
                            <div>
                              <span className="font-medium text-gray-700">Revision:</span>{' '}
                              <span className="text-gray-600">{drawingCheckerData.data.drawing_info.revision}</span>
                            </div>
                          )}
                          {drawingCheckerData.data.drawing_info.material && (
                            <div>
                              <span className="font-medium text-gray-700">Material:</span>{' '}
                              <span className="text-gray-600">{drawingCheckerData.data.drawing_info.material}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Annotated Drawing Preview (Beta) */}
                  {drawingCheckerData.data.annotation_beta?.enabled && (
                    <div className="mt-6 border border-yellow-200 bg-yellow-50 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Annotated Drawing Preview</h3>
                        <span className="px-2 py-0.5 text-xs bg-yellow-400 text-yellow-900 rounded-full font-medium">
                          BETA
                        </span>
                      </div>

                      <p className="text-sm text-gray-600 mb-4">
                        {drawingCheckerData.data.annotation_beta.message}
                      </p>

                      {/* Large Thumbnail Preview */}
                      {drawingCheckerData.data.annotation_beta.annotated_thumbnail_base64 && (
                        <div className="mb-4 border rounded-lg overflow-hidden bg-white">
                          <img
                            src={drawingCheckerData.data.annotation_beta.annotated_thumbnail_base64}
                            alt="Annotated Drawing Preview"
                            className="w-full max-h-[600px] object-contain"
                          />
                        </div>
                      )}

                      {/* Stats */}
                      <div className="text-sm text-gray-600 mb-4">
                        {drawingCheckerData.data.annotation_beta.issues_mapped} of{' '}
                        {drawingCheckerData.data.annotation_beta.issues_total} issues mapped to drawing locations
                        ({Math.round(drawingCheckerData.data.annotation_beta.mapping_rate * 100)}% mapping rate)
                      </div>

                      {/* Download Buttons */}
                      <div className="flex flex-wrap gap-4">
                        {drawingCheckerData.data.annotation_beta.clean_pdf_s3_url && (
                          <a
                            href={drawingCheckerData.data.annotation_beta.clean_pdf_s3_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm font-medium"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            View Original PDF
                          </a>
                        )}

                        {drawingCheckerData.data.annotation_beta.annotated_pdf_s3_url && (
                          <a
                            href={drawingCheckerData.data.annotation_beta.annotated_pdf_s3_url}
                            download
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download Annotated PDF (Beta)
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Top Risks */}
                  {drawingCheckerData.data.top_risks && drawingCheckerData.data.top_risks.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Top Risks
                      </h3>
                      <div className="space-y-3">
                        {drawingCheckerData.data.top_risks.slice(0, 5).map((risk, idx) => (
                          <div
                            key={idx}
                            className={`rounded-xl shadow-sm border overflow-hidden p-4 ${
                              risk.severity === 'CRITICAL'
                                ? 'border-red-300 bg-red-50'
                                : risk.severity === 'MAJOR'
                                  ? 'border-orange-300 bg-orange-50'
                                  : risk.severity === 'MINOR'
                                    ? 'border-green-300 bg-green-50'
                                    : 'border-gray-300 bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={`px-2 py-1 rounded-md text-xs font-bold ${
                                  risk.severity === 'CRITICAL'
                                    ? 'bg-red-600 text-white'
                                    : risk.severity === 'MAJOR'
                                      ? 'bg-orange-500 text-white'
                                      : risk.severity === 'MINOR'
                                        ? 'bg-green-500 text-white'
                                        : 'bg-gray-500 text-white'
                                }`}
                              >
                                {risk.severity}
                              </span>
                              <span className="text-xs text-gray-500 font-mono">{risk.id}</span>
                              <span className="text-xs text-gray-500">{risk.category}</span>
                            </div>
                            <h4 className="font-medium text-gray-900 mt-2">
                              {risk.title}
                            </h4>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Issues Preview (first 5) */}
                  {drawingCheckerData.data.issues && drawingCheckerData.data.issues.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Issues ({drawingCheckerData.data.issues.length})
                        </h3>
                        <button
                          onClick={() => handleViewDrawingCheckerReport('veeco_drawing_checker_v1')}
                          className="text-sm text-green-800 hover:text-green-700 font-medium"
                        >
                          View all in full report &rarr;
                        </button>
                      </div>
                      <div className="space-y-3">
                        {drawingCheckerData.data.issues.slice(0, 5).map((issue, idx) => (
                          <div
                            key={idx}
                            className={`rounded-xl shadow-sm border overflow-hidden p-4 ${
                              issue.severity === 'CRITICAL'
                                ? 'border-red-200 bg-red-50/50'
                                : issue.severity === 'MAJOR'
                                  ? 'border-orange-200 bg-orange-50/50'
                                  : issue.severity === 'MINOR'
                                    ? 'border-green-200 bg-green-50/50'
                                    : 'border-gray-200 bg-gray-50/50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  className={`px-2 py-1 rounded-md text-xs font-bold ${
                                    issue.severity === 'CRITICAL'
                                      ? 'bg-red-600 text-white'
                                      : issue.severity === 'MAJOR'
                                        ? 'bg-orange-500 text-white'
                                        : issue.severity === 'MINOR'
                                          ? 'bg-green-500 text-white'
                                          : 'bg-gray-500 text-white'
                                  }`}
                                >
                                  {issue.severity}
                                </span>
                                <span className="text-xs text-gray-500 font-mono">{issue.id}</span>
                                <span className="px-2 py-1 bg-gray-100 rounded-md text-xs text-gray-600">
                                  {issue.category}
                                </span>
                              </div>
                              {issue.confidence && (
                                <span className="text-xs text-gray-400">
                                  {Math.round(issue.confidence * 100)}% conf
                                </span>
                              )}
                            </div>
                            {issue.location && (
                              <p className="text-xs text-gray-500 mt-1">
                                Location: {issue.location}
                              </p>
                            )}
                            <p className="text-sm text-gray-700 mt-2">
                              {issue.rationale}
                            </p>
                            {issue.recommended_fix && (
                              <p className="text-sm text-green-700 mt-2 border-l-2 border-green-500 pl-2">
                                <span className="font-medium">Fix:</span> {issue.recommended_fix}
                              </p>
                            )}
                          </div>
                        ))}
                        {drawingCheckerData.data.issues.length > 5 && (
                          <div className="text-center py-4">
                            <button
                              onClick={() => handleViewDrawingCheckerReport('veeco_drawing_checker_v1')}
                              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                            >
                              View {drawingCheckerData.data.issues.length - 5} more issues in full report
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Questions */}
                  {drawingCheckerData.data.questions && drawingCheckerData.data.questions.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Clarification Questions
                      </h3>
                      <div className="space-y-3">
                        {drawingCheckerData.data.questions.map((q, idx) => (
                          <div key={idx} className="rounded-xl shadow-sm border border-blue-200 bg-blue-50/50 p-4">
                            <span className="text-xs text-gray-500 font-mono">{q.id}</span>
                            <h4 className="font-medium text-gray-900 mt-1">{q.question}</h4>
                            {q.context && (
                              <p className="text-sm text-gray-600 mt-1">{q.context}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Drawing Checker V2 Tab */}
              {activeTab === 'drawing_checker_v2' && drawingCheckerV2Data?.data && (
                <div className="space-y-6">
                  {/* Quality Score Summary */}
                  {drawingCheckerV2Data.data.summary && (
                    <div className="rounded-xl shadow-sm border border-gray-100 overflow-hidden p-6 bg-indigo-50">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Quality Summary
                          </h3>
                          <span className="px-2 py-0.5 text-xs bg-indigo-500 text-white rounded-full font-medium">V2</span>
                        </div>
                        <button
                          onClick={() => handleViewDrawingCheckerReport('veeco_drawing_checker_v2')}
                          className="px-4 py-2 bg-indigo-700 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 transition-colors"
                        >
                          View Full Report
                        </button>
                      </div>
                      <p className="text-gray-600 text-sm mb-4">
                        {drawingCheckerV2Data.data.drawing_info?.description || 'Enhanced drawing validation with feedback and drill-down'}
                      </p>
                      <div className="grid grid-cols-5 gap-4">
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className={`text-3xl font-bold ${
                            (drawingCheckerV2Data.data.summary.quality_score || 0) >= 7
                              ? 'text-green-600'
                              : (drawingCheckerV2Data.data.summary.quality_score || 0) >= 5
                                ? 'text-yellow-600'
                                : 'text-red-600'
                          }`}>
                            {drawingCheckerV2Data.data.summary.quality_score || 0}
                            <span className="text-sm text-gray-400">/{drawingCheckerV2Data.data.summary.quality_max || 10}</span>
                          </div>
                          <div className="text-xs text-gray-500">Quality Score</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-red-600">
                            {drawingCheckerV2Data.data.summary.critical || 0}
                          </div>
                          <div className="text-xs text-gray-500">Critical</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-orange-600">
                            {drawingCheckerV2Data.data.summary.major || 0}
                          </div>
                          <div className="text-xs text-gray-500">Major</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-green-600">
                            {drawingCheckerV2Data.data.summary.minor || 0}
                          </div>
                          <div className="text-xs text-gray-500">Minor</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-gray-600">
                            {drawingCheckerV2Data.data.summary.total_issues || 0}
                          </div>
                          <div className="text-xs text-gray-500">Total Issues</div>
                        </div>
                      </div>
                      {drawingCheckerV2Data.data.drawing_info && (
                        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                          {drawingCheckerV2Data.data.drawing_info.drawing_number && (
                            <div>
                              <span className="font-medium text-gray-700">Drawing #:</span>{' '}
                              <span className="text-gray-600">{drawingCheckerV2Data.data.drawing_info.drawing_number}</span>
                            </div>
                          )}
                          {drawingCheckerV2Data.data.drawing_info.revision && (
                            <div>
                              <span className="font-medium text-gray-700">Revision:</span>{' '}
                              <span className="text-gray-600">{drawingCheckerV2Data.data.drawing_info.revision}</span>
                            </div>
                          )}
                          {drawingCheckerV2Data.data.drawing_info.material && (
                            <div>
                              <span className="font-medium text-gray-700">Material:</span>{' '}
                              <span className="text-gray-600">{drawingCheckerV2Data.data.drawing_info.material}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Annotated Drawing Preview */}
                  {drawingCheckerV2Data.data.annotation_beta?.enabled && (() => {
                    const ab = drawingCheckerV2Data.data.annotation_beta
                    const effectiveStatus = annotationOverride?.annotation_status || ab.annotation_status || 'complete'
                    const effectiveUrls = annotationOverride?.annotated_image_urls || ab.annotated_image_urls || []
                    const effectiveThumbnail = annotationOverride?.annotated_thumbnail_base64 || ab.annotated_thumbnail_base64 || ''

                    return (
                      <div className="mt-6 border border-indigo-200 bg-indigo-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-4">
                          <h3 className="text-lg font-semibold text-gray-900">Annotated Drawing</h3>
                          <span className="px-2 py-0.5 text-xs bg-indigo-400 text-white rounded-full font-medium">
                            Vision AI
                          </span>
                        </div>

                        {effectiveStatus === 'pending' && (
                          <div className="flex items-center gap-3 mb-4 p-3 bg-indigo-100 rounded-lg">
                            <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm text-indigo-700 font-medium">
                              Generating annotated drawings... This will appear automatically when ready.
                            </span>
                          </div>
                        )}

                        {effectiveStatus === 'failed' && (
                          <div className="mb-4 p-3 bg-gray-100 rounded-lg">
                            <span className="text-sm text-gray-500">
                              Annotation generation was unsuccessful. Issue analysis is still complete.
                            </span>
                          </div>
                        )}

                        {effectiveStatus === 'complete' && (
                          <>
                            <p className="text-sm text-gray-600 mb-4">{ab.message}</p>
                            {effectiveThumbnail && (
                              <div className="mb-4 border rounded-lg overflow-hidden bg-white">
                                <img
                                  src={effectiveThumbnail}
                                  alt="Annotated Drawing"
                                  className="w-full max-h-[600px] object-contain"
                                />
                              </div>
                            )}
                            <div className="text-sm text-gray-600 mb-4">
                              {ab.issues_mapped} of {ab.issues_total} issues mapped
                              ({Math.round((ab.mapping_rate || 0) * 100)}% mapping rate)
                            </div>
                          </>
                        )}

                        <div className="flex flex-wrap gap-4">
                          {ab.clean_pdf_s3_url && (
                            <a
                              href={ab.clean_pdf_s3_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm font-medium"
                            >
                              View Original PDF
                            </a>
                          )}
                          {effectiveUrls.length > 0 && (
                            effectiveUrls.map((url, idx) => (
                              <a
                                key={idx}
                                href={url}
                                download
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 text-sm font-medium"
                              >
                                {effectiveUrls.length === 1
                                  ? 'Download Annotated Image'
                                  : `Download Page ${idx + 1}`}
                              </a>
                            ))
                          )}
                        </div>
                      </div>
                    )
                  })()}

                  {/* Issues with Feedback + Drill-Down */}
                  {drawingCheckerV2Data.data.issues && drawingCheckerV2Data.data.issues.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Issues ({drawingCheckerV2Data.data.issues.length})
                        </h3>
                        <button
                          onClick={() => handleViewDrawingCheckerReport('veeco_drawing_checker_v2')}
                          className="text-sm text-indigo-700 hover:text-indigo-600 font-medium"
                        >
                          View all in full report &rarr;
                        </button>
                      </div>
                      <div className="space-y-3">
                        {drawingCheckerV2Data.data.issues.map((issue, idx) => (
                          <div
                            key={idx}
                            className={`rounded-xl shadow-sm border overflow-hidden p-4 ${
                              issue.severity === 'CRITICAL'
                                ? 'border-red-200 bg-red-50/50'
                                : issue.severity === 'MAJOR'
                                  ? 'border-orange-200 bg-orange-50/50'
                                  : issue.severity === 'MINOR'
                                    ? 'border-green-200 bg-green-50/50'
                                    : 'border-gray-200 bg-gray-50/50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  className={`px-2 py-1 rounded-md text-xs font-bold ${
                                    issue.severity === 'CRITICAL'
                                      ? 'bg-red-600 text-white'
                                      : issue.severity === 'MAJOR'
                                        ? 'bg-orange-500 text-white'
                                        : issue.severity === 'MINOR'
                                          ? 'bg-green-500 text-white'
                                          : 'bg-gray-500 text-white'
                                  }`}
                                >
                                  {issue.severity}
                                </span>
                                <span className="text-xs text-gray-500 font-mono">{issue.id}</span>
                                <span className="px-2 py-1 bg-gray-100 rounded-md text-xs text-gray-600">
                                  {issue.category}
                                </span>
                              </div>
                              {/* Confidence Label (V2: evidence-based label instead of %) */}
                              {issue.confidence_label ? (
                                <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                                  issue.confidence_label === 'Verified'
                                    ? 'bg-green-100 text-green-700'
                                    : issue.confidence_label === 'Likely'
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {issue.confidence_label}
                                </span>
                              ) : issue.confidence ? (
                                <span className="text-xs text-gray-400">
                                  {Math.round(issue.confidence * 100)}% conf
                                </span>
                              ) : null}
                            </div>
                            {issue.location && (
                              <p className="text-xs text-gray-500 mt-1">
                                Location: {issue.location}
                              </p>
                            )}
                            <p className="text-sm text-gray-700 mt-2">
                              {issue.rationale}
                            </p>
                            {issue.recommended_fix && (
                              <p className="text-sm text-green-700 mt-2 border-l-2 border-green-500 pl-2">
                                <span className="font-medium">Fix:</span> {issue.recommended_fix}
                              </p>
                            )}

                            {/* V2: Feedback Buttons */}
                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                              <span className="text-xs text-gray-400 mr-1">Feedback:</span>
                              {['interesting', 'neutral', 'not_interesting'].map((fb) => (
                                <button
                                  key={fb}
                                  onClick={async () => {
                                    const prev = feedbackState[issue.id]
                                    setFeedbackState(s => ({ ...s, [issue.id]: fb }))
                                    try {
                                      await axios.post(`${BASE_URL}/ocr/issue-feedback/`, {
                                        analysis_id: data?._id,
                                        issue_id: issue.id,
                                        feedback: fb,
                                        severity: issue.severity,
                                        category: issue.category,
                                        source: issue.source,
                                        drawing_number: drawingCheckerV2Data.data.drawing_info?.drawing_number || '',
                                      }, { headers: { Authorization: `Bearer ${token}` } })
                                    } catch {
                                      setFeedbackState(s => ({ ...s, [issue.id]: prev }))
                                      toast.error('Failed to save feedback')
                                    }
                                  }}
                                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                    feedbackState[issue.id] === fb
                                      ? fb === 'interesting'
                                        ? 'bg-green-600 text-white'
                                        : fb === 'not_interesting'
                                          ? 'bg-red-500 text-white'
                                          : 'bg-gray-500 text-white'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  {fb === 'interesting' ? '👍 Useful' : fb === 'not_interesting' ? '👎 Not Useful' : '😐 Neutral'}
                                </button>
                              ))}

                              {/* V2: Drill-Down Button */}
                              <button
                                onClick={async () => {
                                  const issueId = issue.id

                                  if (drillDownState[issueId]?.data) {
                                    setDrillDownState(s => ({
                                      ...s,
                                      [issueId]: { ...s[issueId], collapsed: !s[issueId]?.collapsed }
                                    }))
                                    return
                                  }

                                  const startTime = Date.now()
                                  const issuePage = issue.page || 1

                                  const LOG_SCRIPT = [
                                    { delay: 0,     level: 'phase',   msg: `Initiating deep-dive on issue ${issueId}...` },
                                    { delay: 900,   level: 'info',    msg: 'Uploading drawing to SourceOptima Vision AI...' },
                                    { delay: 2400,  level: 'info',    msg: `Parsing page ${issuePage} geometry and annotations...` },
                                    { delay: 4500,  level: 'info',    msg: 'Evaluating manufacturing and assembly impact...' },
                                    { delay: 7000,  level: 'info',    msg: 'Generating solution options...' },
                                    { delay: 9500,  level: 'info',    msg: 'Cross-referencing engineering standards...' },
                                    { delay: 12000, level: 'info',    msg: 'Assessing cascading concerns...' },
                                    { delay: 14500, level: 'info',    msg: 'Compiling final recommendations...' },
                                  ]

                                  setDrillDownState(s => ({ ...s, [issueId]: { loading: true, logs: [] } }))

                                  drillDownTimersRef.current[issueId] = LOG_SCRIPT.map(({ delay, level, msg }) =>
                                    setTimeout(() => {
                                      const t = (Date.now() - startTime) / 1000
                                      setDrillDownState(s =>
                                        s[issueId]?.loading
                                          ? { ...s, [issueId]: { ...s[issueId], logs: [...(s[issueId].logs || []), { t, level, msg }] } }
                                          : s
                                      )
                                    }, delay)
                                  )

                                  try {
                                    const res = await axios.post(`${BASE_URL}/ocr/drill-down/`, {
                                      file_id: data?._id,
                                      issue: issue,
                                    }, { headers: { Authorization: `Bearer ${token}` } })
                                    ;(drillDownTimersRef.current[issueId] || []).forEach(clearTimeout)
                                    const finalT = (Date.now() - startTime) / 1000
                                    setDrillDownState(s => ({
                                      ...s,
                                      [issueId]: {
                                        loading: false,
                                        data: res.data.drill_down,
                                        collapsed: false,
                                        logs: [...(s[issueId]?.logs || []), { t: finalT, level: 'success', msg: '✔ Analysis complete' }],
                                      }
                                    }))
                                  } catch (err) {
                                    ;(drillDownTimersRef.current[issueId] || []).forEach(clearTimeout)
                                    setDrillDownState(s => ({
                                      ...s,
                                      [issueId]: { loading: false, error: err.response?.data?.error || 'Drill-down failed' }
                                    }))
                                    toast.error('Drill-down analysis failed')
                                  }
                                }}
                                disabled={drillDownState[issue.id]?.loading}
                                className="ml-auto px-3 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium hover:bg-indigo-200 transition-colors disabled:opacity-50"
                              >
                                {drillDownState[issue.id]?.loading
                                  ? 'Analyzing...'
                                  : drillDownState[issue.id]?.data
                                    ? (drillDownState[issue.id]?.collapsed ? 'Show Details' : 'Hide Details')
                                    : '🔍 Drill Down'}
                              </button>
                            </div>

                            {/* V2: Drill-Down Terminal (visible while loading) */}
                            {drillDownState[issue.id]?.loading && (
                              <div className="mt-3 rounded-lg bg-[#1e5045] p-3 font-mono text-xs max-h-44 overflow-y-auto">
                                <div className="text-[#f6f4ed]/50 mb-2 text-[10px] uppercase tracking-widest">SourceOptima Vision AI — Activity Log</div>
                                {(drillDownState[issue.id].logs || []).map((entry, i) => (
                                  <div key={i} className={`py-0.5 ${DD_LOG_COLORS[entry.level] || 'text-gray-400'}`}>
                                    <span className="text-[#f6f4ed]/40 mr-2">[{entry.t.toFixed(1).padStart(6)}s]</span>
                                    {entry.msg}
                                  </div>
                                ))}
                                <div className="text-[#94c43d] animate-pulse mt-1">▌</div>
                              </div>
                            )}

                            {/* V2: Drill-Down Result Panel */}
                            {drillDownState[issue.id]?.data && !drillDownState[issue.id]?.collapsed && (
                              <DrillDownPanel data={drillDownState[issue.id].data} />
                            )}

                            {drillDownState[issue.id]?.error && (
                              <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-600">
                                {drillDownState[issue.id].error}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Questions */}
                  {drawingCheckerV2Data.data.questions && drawingCheckerV2Data.data.questions.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Clarification Questions
                      </h3>
                      <div className="space-y-3">
                        {drawingCheckerV2Data.data.questions.map((q, idx) => (
                          <div key={idx} className="rounded-xl shadow-sm border border-blue-200 bg-blue-50/50 p-4">
                            <span className="text-xs text-gray-500 font-mono">{q.id}</span>
                            <h4 className="font-medium text-gray-900 mt-1">{q.question}</h4>
                            {q.context && (
                              <p className="text-sm text-gray-600 mt-1">{q.context}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Drawing Checker Veeco Tab */}
              {activeTab === 'drawing_checker_veeco' && drawingCheckerVeecoData?.data && (
                <div className="space-y-6">
                  {/* Quality Score Summary */}
                  {drawingCheckerVeecoData.data.summary && (
                    <div className="rounded-xl shadow-sm border border-gray-100 overflow-hidden p-6 bg-purple-50">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Veeco Quality Summary
                        </h3>
                        <button
                          onClick={() => handleViewDrawingCheckerReport('veeco_drawing_checker_veeco_v1')}
                          className="px-4 py-2 bg-purple-700 text-white rounded-lg text-sm font-medium hover:bg-purple-600 transition-colors"
                        >
                          View Full Report
                        </button>
                      </div>
                      <p className="text-gray-600 text-sm mb-4">
                        {drawingCheckerVeecoData.data.drawing_info?.description || 'Veeco-specific drawing validation'}
                      </p>
                      <div className="grid grid-cols-5 gap-4">
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className={`text-3xl font-bold ${
                            (drawingCheckerVeecoData.data.summary.quality_score || 0) >= 7
                              ? 'text-green-600'
                              : (drawingCheckerVeecoData.data.summary.quality_score || 0) >= 5
                                ? 'text-yellow-600'
                                : 'text-red-600'
                          }`}>
                            {drawingCheckerVeecoData.data.summary.quality_score || 0}
                            <span className="text-sm text-gray-400">/{drawingCheckerVeecoData.data.summary.quality_max || 10}</span>
                          </div>
                          <div className="text-xs text-gray-500">Quality Score</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-red-600">
                            {drawingCheckerVeecoData.data.summary.critical || 0}
                          </div>
                          <div className="text-xs text-gray-500">Critical</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-orange-600">
                            {drawingCheckerVeecoData.data.summary.major || 0}
                          </div>
                          <div className="text-xs text-gray-500">Major</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-green-600">
                            {drawingCheckerVeecoData.data.summary.minor || 0}
                          </div>
                          <div className="text-xs text-gray-500">Minor</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-gray-600">
                            {drawingCheckerVeecoData.data.summary.total_issues || 0}
                          </div>
                          <div className="text-xs text-gray-500">Total Issues</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Issues */}
                  {drawingCheckerVeecoData.data.issues && drawingCheckerVeecoData.data.issues.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        Veeco-Specific Issues ({drawingCheckerVeecoData.data.issues.length})
                      </h3>
                      <div className="space-y-3">
                        {drawingCheckerVeecoData.data.issues.map((issue, idx) => (
                          <div
                            key={idx}
                            className={`rounded-xl shadow-sm border overflow-hidden p-4 ${
                              issue.severity === 'CRITICAL'
                                ? 'border-red-200 bg-red-50/50'
                                : issue.severity === 'MAJOR'
                                  ? 'border-orange-200 bg-orange-50/50'
                                  : issue.severity === 'MINOR'
                                    ? 'border-green-200 bg-green-50/50'
                                    : 'border-gray-200 bg-gray-50/50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  className={`px-2 py-1 rounded-md text-xs font-bold ${
                                    issue.severity === 'CRITICAL'
                                      ? 'bg-red-600 text-white'
                                      : issue.severity === 'MAJOR'
                                        ? 'bg-orange-500 text-white'
                                        : issue.severity === 'MINOR'
                                          ? 'bg-green-500 text-white'
                                          : 'bg-gray-500 text-white'
                                  }`}
                                >
                                  {issue.severity}
                                </span>
                                <span className="text-xs text-gray-500 font-mono">{issue.id}</span>
                                <span className="px-2 py-1 bg-purple-100 rounded-md text-xs text-purple-600">
                                  Veeco
                                </span>
                              </div>
                            </div>
                            {issue.location && (
                              <p className="text-xs text-gray-500 mt-1">
                                Location: {issue.location}
                              </p>
                            )}
                            <p className="text-sm text-gray-700 mt-2">
                              {issue.rationale}
                            </p>
                            {issue.recommended_fix && (
                              <p className="text-sm text-green-700 mt-2 border-l-2 border-green-500 pl-2">
                                <span className="font-medium">Fix:</span> {issue.recommended_fix}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Drawing Checker General Tab */}
              {activeTab === 'drawing_checker_general' && drawingCheckerGeneralData?.data && (
                <div className="space-y-6">
                  {/* Quality Score Summary */}
                  {drawingCheckerGeneralData.data.summary && (
                    <div className="rounded-xl shadow-sm border border-gray-100 overflow-hidden p-6 bg-blue-50">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          General Quality Summary
                        </h3>
                        <button
                          onClick={() => handleViewDrawingCheckerReport('veeco_drawing_checker_general_v1')}
                          className="px-4 py-2 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
                        >
                          View Full Report
                        </button>
                      </div>
                      <p className="text-gray-600 text-sm mb-4">
                        {drawingCheckerGeneralData.data.drawing_info?.description || 'General drawing validation'}
                      </p>
                      <div className="grid grid-cols-5 gap-4">
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className={`text-3xl font-bold ${
                            (drawingCheckerGeneralData.data.summary.quality_score || 0) >= 7
                              ? 'text-green-600'
                              : (drawingCheckerGeneralData.data.summary.quality_score || 0) >= 5
                                ? 'text-yellow-600'
                                : 'text-red-600'
                          }`}>
                            {drawingCheckerGeneralData.data.summary.quality_score || 0}
                            <span className="text-sm text-gray-400">/{drawingCheckerGeneralData.data.summary.quality_max || 10}</span>
                          </div>
                          <div className="text-xs text-gray-500">Quality Score</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-red-600">
                            {drawingCheckerGeneralData.data.summary.critical || 0}
                          </div>
                          <div className="text-xs text-gray-500">Critical</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-orange-600">
                            {drawingCheckerGeneralData.data.summary.major || 0}
                          </div>
                          <div className="text-xs text-gray-500">Major</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-green-600">
                            {drawingCheckerGeneralData.data.summary.minor || 0}
                          </div>
                          <div className="text-xs text-gray-500">Minor</div>
                        </div>
                        <div className="text-center p-3 bg-white rounded-lg">
                          <div className="text-2xl font-bold text-gray-600">
                            {drawingCheckerGeneralData.data.summary.total_issues || 0}
                          </div>
                          <div className="text-xs text-gray-500">Total Issues</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Issues */}
                  {drawingCheckerGeneralData.data.issues && drawingCheckerGeneralData.data.issues.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        General Issues ({drawingCheckerGeneralData.data.issues.length})
                      </h3>
                      <div className="space-y-3">
                        {drawingCheckerGeneralData.data.issues.map((issue, idx) => (
                          <div
                            key={idx}
                            className={`rounded-xl shadow-sm border overflow-hidden p-4 ${
                              issue.severity === 'CRITICAL'
                                ? 'border-red-200 bg-red-50/50'
                                : issue.severity === 'MAJOR'
                                  ? 'border-orange-200 bg-orange-50/50'
                                  : issue.severity === 'MINOR'
                                    ? 'border-green-200 bg-green-50/50'
                                    : 'border-gray-200 bg-gray-50/50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  className={`px-2 py-1 rounded-md text-xs font-bold ${
                                    issue.severity === 'CRITICAL'
                                      ? 'bg-red-600 text-white'
                                      : issue.severity === 'MAJOR'
                                        ? 'bg-orange-500 text-white'
                                        : issue.severity === 'MINOR'
                                          ? 'bg-green-500 text-white'
                                          : 'bg-gray-500 text-white'
                                  }`}
                                >
                                  {issue.severity}
                                </span>
                                <span className="text-xs text-gray-500 font-mono">{issue.id}</span>
                                <span className="px-2 py-1 bg-blue-100 rounded-md text-xs text-blue-600">
                                  General
                                </span>
                              </div>
                            </div>
                            {issue.location && (
                              <p className="text-xs text-gray-500 mt-1">
                                Location: {issue.location}
                              </p>
                            )}
                            <p className="text-sm text-gray-700 mt-2">
                              {issue.rationale}
                            </p>
                            {issue.recommended_fix && (
                              <p className="text-sm text-green-700 mt-2 border-l-2 border-green-500 pl-2">
                                <span className="font-medium">Fix:</span> {issue.recommended_fix}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Calculator Tab */}
              {activeTab === 'calculator' && tariffData?.data && (
                <TariffCalculator
                  fileId={data?._id}
                  initialValues={{
                    htsCode: tariffData.data.tradeCompliance?.primaryHts?.code || '',
                    generalDutyPercent: tariffData.data.tradeCompliance?.primaryHts?.generalDutyRatePercent || 0,
                    material: tariffData.data.materialSpecifications?.material || '',
                    materialParent: tariffData.data.materialSpecifications?.materialParent || '',
                    sec232Info: tariffData.data.tradeCompliance?.primaryHts?.sec232,
                    sec301Info: tariffData.data.tradeCompliance?.primaryHts?.sec301China,
                  }}
                />
              )}

              {/* Weight Estimation Tab */}
              {activeTab === 'weight' && weightData?.data && (
                <WeightResult
                  data={weightData.data}
                  success={weightData.success !== false}
                  error={weightData.error}
                  metadata={weightData.metadata}
                />
              )}

              {/* No data state */}
              {!tariffData && !costData && !designReviewData && !drawingCheckerData && !drawingCheckerVeecoData && !drawingCheckerGeneralData && !drawingCheckerV2Data && !weightData && (
                <div className="text-center py-12">
                  <IoDocument className="w-12 h-12 text-gray-300 mx-auto" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900">
                    No Analysis Data
                  </h3>
                  <p className="mt-2 text-gray-500">
                    Run an analysis on this file to see results
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}




